import { theme, type ThemeConfig } from 'antd';
import { brand, fontFamily, type ResolvedMode } from './tokens';

/** The sidebar and mobile nav are always Deep Teal, in both modes, so the brand reads the same as the app's nav bar. */
const navMenu: NonNullable<ThemeConfig['components']>['Menu'] = {
  darkItemBg: brand.background,
  darkSubMenuItemBg: brand.background,
  darkPopupBg: brand.surface,
  darkItemColor: brand.textSecondary,
  darkItemHoverColor: brand.textPrimary,
  darkItemHoverBg: 'rgba(255, 255, 255, 0.06)',
  // Same tonal indicator as the app's NavigationBar (primary at 24%).
  darkItemSelectedBg: 'rgba(20, 184, 166, 0.24)',
  darkItemSelectedColor: brand.primaryLight,
  darkGroupTitleColor: brand.muted,
  itemBorderRadius: 10,
  itemMarginInline: 8,
  collapsedIconSize: 18,
};

const shared: ThemeConfig['token'] = {
  fontFamily,
  fontSize: 14,
  borderRadius: 10,
  borderRadiusLG: 16,
  borderRadiusSM: 8,
  colorSuccess: brand.success,
  colorWarning: brand.gold,
  colorInfo: brand.accentBlue,
  wireframe: false,
};

const dark: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...shared,
    // Seeds first so derived fills/hover tones stay teal, then explicit map tokens that mirror the app's ColorScheme.
    colorBgBase: brand.background,
    colorTextBase: brand.textPrimary,
    colorPrimary: brand.primary,
    colorLink: brand.primaryLight,
    colorError: brand.danger,
    colorBgLayout: brand.background,
    colorBgContainer: brand.surface,
    colorBgElevated: brand.surfaceHigh,
    colorBgSpotlight: brand.surfaceHighest,
    colorBorder: brand.divider,
    colorBorderSecondary: brand.outlineVariant,
    colorSplit: brand.outlineVariant,
    colorText: brand.textPrimary,
    colorTextSecondary: brand.textSecondary,
    colorTextTertiary: brand.muted,
    colorTextQuaternary: 'rgba(181, 207, 205, 0.45)',
    colorFillAlter: brand.surfaceLow,
  },
  components: {
    Layout: { bodyBg: brand.background, headerBg: brand.background, siderBg: brand.background },
    Menu: navMenu,
    // Filled teal buttons carry dark text, exactly like the app's FilledButton.
    Button: { primaryColor: brand.onPrimary, primaryShadow: 'none', dangerShadow: 'none', defaultShadow: 'none', fontWeight: 500 },
    Card: { headerBg: 'transparent' },
    Table: {
      headerBg: brand.surfaceHigh,
      headerColor: brand.textPrimary,
      headerSortActiveBg: brand.surfaceHighest,
      headerSortHoverBg: brand.surfaceHighest,
      rowHoverBg: brand.surfaceLow,
      rowSelectedBg: 'rgba(20, 184, 166, 0.16)',
      rowSelectedHoverBg: 'rgba(20, 184, 166, 0.22)',
      borderColor: brand.outlineVariant,
      headerSplitColor: brand.divider,
      stickyScrollBarBg: brand.divider,
    },
    Modal: { contentBg: brand.surface, headerBg: brand.surface, footerBg: brand.surface },
    // Same surface as the app's bottom sheets and dialogs.
    Drawer: { colorBgElevated: brand.surface },
    Segmented: {
      trackBg: brand.backgroundDeep,
      itemSelectedBg: brand.primary,
      itemSelectedColor: brand.onPrimary,
      itemColor: brand.textSecondary,
      itemHoverColor: brand.textPrimary,
    },
    Tabs: { itemSelectedColor: brand.primaryLight, inkBarColor: brand.primary, itemHoverColor: brand.primaryLight },
    Switch: { handleBg: brand.textPrimary },
    Tooltip: { colorBgSpotlight: '#E6F4F2', colorTextLightSolid: brand.background },
    Tag: { defaultBg: brand.surfaceHigh, defaultColor: brand.textPrimary },
    Statistic: { contentFontSize: 26 },
    Progress: { defaultColor: brand.primary, remainingColor: brand.surfaceHigh },
  },
};

const light: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...shared,
    // The darker tonal teal: AA for white text on primary buttons and for teal links on white.
    colorPrimary: brand.primaryContainer,
    colorLink: brand.primaryContainer,
    colorError: '#D93636',
    colorBgLayout: '#F3F8F8',
    colorBgContainer: '#FFFFFF',
    colorBorder: '#CFE0DF',
    colorBorderSecondary: '#E2EDEC',
    colorText: '#0B2A2B',
    colorTextSecondary: '#46625F',
    colorTextTertiary: '#6B8784',
  },
  components: {
    Layout: { bodyBg: '#F3F8F8', headerBg: '#FFFFFF', siderBg: brand.background },
    Menu: navMenu,
    Button: { primaryShadow: 'none', dangerShadow: 'none', defaultShadow: 'none', fontWeight: 500 },
    Card: { headerBg: 'transparent' },
    Table: { headerBg: '#EAF3F2', rowHoverBg: '#F3F8F8', rowSelectedBg: '#E3F4F1', rowSelectedHoverBg: '#D6EFEB' },
    Segmented: { itemSelectedBg: brand.primaryContainer, itemSelectedColor: '#FFFFFF', trackBg: '#E2EDEC' },
    Statistic: { contentFontSize: 26 },
    Progress: { defaultColor: brand.primaryContainer },
  },
};

export function buildTheme(mode: ResolvedMode): ThemeConfig {
  return mode === 'dark' ? dark : light;
}
